import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, Mountain, Calendar, ArrowRight, Compass } from "lucide-react";
import { Button } from "../components/ui/button";
import { places } from "../lib/places";
import ShareWhatsAppButton from "../components/ShareWhatsAppButton";

export default function PlaceDetail() {
  const { id } = useParams();
  const place = places[id];

  if (!place) {
    return (
      <main className="pt-32 pb-24 text-center px-6 min-h-screen">
        <h1 className="font-serif text-4xl text-[#1F4D3A] mb-4">Place not found</h1>
        <p className="text-[#5A4A38] mb-6">This place is not yet in our library. Try again soon.</p>
        <Link to="/" className="text-[#C87A50] font-semibold hover:underline">← Back to home</Link>
      </main>
    );
  }

  return (
    <main className="pt-20 sm:pt-24 pb-16">
      {/* Hero */}
      <section className="relative h-[60vh] sm:h-[70vh] overflow-hidden">
        <img src={place.image} alt={place.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F4D3A]/90 via-[#1F4D3A]/25 to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-10 sm:pb-14">
          <Link to="/" className="inline-flex items-center gap-2 text-[#E5DDCB] hover:text-white text-sm mb-4 sm:mb-6 w-fit">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <span className="inline-block bg-[#C87A50] text-white text-[10px] sm:text-xs uppercase tracking-widest font-semibold px-3 py-1 rounded-full mb-3 sm:mb-4 self-start">
            {place.category}
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl font-bold text-white leading-tight max-w-3xl">
            {place.name}
          </h1>
          <p className="text-[#E5DDCB] text-base sm:text-lg mt-2 sm:mt-3 max-w-2xl italic font-serif">
            {place.tagline}
          </p>
          <div className="flex items-center gap-2 mt-4 text-white/90 text-sm">
            <MapPin className="w-4 h-4" /> {place.location}
          </div>
          <div className="mt-5">
            <ShareWhatsAppButton
              title={place.name}
              subtitle={place.tagline}
              path={`/places/${id}`}
              variant="inline"
              testId="place-detail-share-btn"
            />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12 mt-12 sm:mt-16">
        <div className="lg:col-span-2 space-y-10 sm:space-y-12">
          <div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F4D3A] mb-3 sm:mb-4">About {place.name}</h2>
            <p className="text-[#4A3728] leading-relaxed text-base sm:text-lg">{place.description}</p>
          </div>

          {/* Highlights */}
          <div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F4D3A] mb-4 sm:mb-5">What to see & do</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {place.highlights.map((h) => (
                <div key={h} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#E5DDCB]">
                  <div className="w-6 h-6 rounded-full bg-[#F5F0E8] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Compass className="w-3.5 h-3.5 text-[#1F4D3A]" />
                  </div>
                  <span className="text-sm text-[#4A3728]">{h}</span>
                </div>
              ))}
            </div>
          </div>

          {/* How to reach */}
          <div className="bg-white rounded-2xl border border-[#E5DDCB] p-5 sm:p-6">
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#1F4D3A] mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#C87A50]" /> How to reach
            </h3>
            <p className="text-sm sm:text-base text-[#4A3728] leading-relaxed">{place.howToReach}</p>
          </div>

          {/* Traveller tips */}
          {place.tips && place.tips.length > 0 && (
            <div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#1F4D3A] mb-4">Traveller tips</h3>
              <ul className="space-y-2">
                {place.tips.map((t, i) => (
                  <li key={`${i}-${t.slice(0, 20)}`} className="flex items-start gap-3 text-sm text-[#4A3728]">
                    <span className="text-[#C87A50] font-bold flex-shrink-0">{String(i + 1).padStart(2, '0')}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar — quick facts + CTA */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24 space-y-4">
            <div className="bg-white rounded-2xl border border-[#E5DDCB] p-5 sm:p-6">
              <h3 className="font-serif text-lg sm:text-xl font-bold text-[#1F4D3A] mb-4 pb-3 border-b border-[#E5DDCB]">
                Quick facts
              </h3>
              <ul className="space-y-3">
                {place.quickFacts.map((f) => (
                  <li key={f.label} className="flex items-center justify-between text-sm">
                    <span className="text-[10px] uppercase tracking-widest text-[#6B8E5A] font-semibold">
                      {f.label}
                    </span>
                    <span className="text-[#4A3728] font-medium">{f.value}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#1F4D3A] rounded-2xl p-5 sm:p-6 text-[#F5F0E8]">
              <Compass className="w-8 h-8 text-[#C9A961] mb-3" />
              <h3 className="font-serif text-xl font-bold text-white mb-2 leading-tight">
                Plan a trip to {place.name}
              </h3>
              <p className="text-sm text-[#D9CDB0] mb-4">
                Tell us your dates and we'll design a curated journey around it.
              </p>
              <Link to="/contact">
                <Button className="btn-primary w-full bg-[#C87A50] hover:bg-[#B4653A] text-white rounded-full h-11">
                  Enquire now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <Link to="/tours">
              <Button variant="outline" className="w-full rounded-full h-11 border-[#1F4D3A] text-[#1F4D3A] hover:bg-[#1F4D3A] hover:text-white">
                Browse all trips
              </Button>
            </Link>
          </div>
        </aside>
      </section>
    </main>
  );
}
